import {
  ColumnDef,
  ColumnSizingState,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingFn,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { com } from '@eidu/web'
import { NavigateFunction } from 'react-router-dom'
import { Button, Checkbox, Chip, Group, IconButton, iconSizes, Text, TextInput } from '@common/ui'
import { IconArrowDown, IconArrowUp, IconDotsVertical, IconFilter } from '@common/ui/icons'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityWithLabel from '../../domain/entity/EntityWithLabel'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import useHasGlobalPermissions from '../../pages/permission/useHasGlobalPermissions'
import useEntityCount from '../../io/useEntityCount'
import getRenderFieldCell, { CellContent } from '../misc/getRenderFieldCell'
import getRenderInvitationCell from '../misc/getRenderInvitationCell'
import BulkEditMenu from './BulkEditMenu'
import FilterPopover, { NULL_FILTER_VALUE } from './FilterPopover'
import InfiniteScrollTable from './InfiniteScrollTable'
import TablePanel from './TablePanel'
import useDebounced from '../../ui/debounce/useDebounced'
import EntityType = com.eidu.entity.type.EntityType
import EntityId = com.eidu.entity.EntityId
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import Field = com.eidu.entity.field.Field
import FieldId = com.eidu.entity.field.FieldId
import FieldType = com.eidu.entity.field.FieldType
import entityIdFromStringOrNull = com.eidu.entity.entityIdFromStringOrNull
import entityIdFromString = com.eidu.entity.entityIdFromString
import phoneNumberLoginIdentifierKey = com.eidu.entity.phoneNumberLoginIdentifierKey
import emailLoginIdentifierKey = com.eidu.entity.emailLoginIdentifierKey
import GlobalAction = com.eidu.entity.permission.GlobalAction
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import invitationStatusKey = com.eidu.entity.invitationStatusKey

const SortIndicator = ({ direction }: { direction: false | 'asc' | 'desc' }) => {
  if (direction === 'asc') return <IconArrowUp size={iconSizes.xs} stroke={1.5} />
  if (direction === 'desc') return <IconArrowDown size={iconSizes.xs} stroke={1.5} />
  return null
}

const getIdMap = (entities: readonly EntityWithLabelAndRelated[]): ReadonlyMap<EntityId, EntityWithLabel> =>
  new EqualityHashMap(
    entities.flatMap((entity) => [
      [entity.entity.id, entity] as [EntityId, EntityWithLabel],
      ...entity.referencedEntities.map((ref) => [ref.entity.id, ref] as [EntityId, EntityWithLabel]),
    ])
  )

const naturalCompare = (a: string | undefined | null, b: string | undefined | null): number => {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

const naturalSortingFn: SortingFn<TableRowData> = (rowA, rowB, columnId) => {
  const a = rowA.original.sortValues[columnId] ?? (rowA.getValue(columnId) as string | undefined)
  const b = rowB.original.sortValues[columnId] ?? (rowB.getValue(columnId) as string | undefined)
  return naturalCompare(a, b)
}

type TableRowData = {
  entityId: string
  rowNumber: number
  entity: EntityWithLabelAndRelated
  fieldValues: Record<string, string | undefined>
  sortValues: Record<string, string | undefined>
  phoneNumberLoginIdentifier?: string
  emailLoginIdentifier?: string
  invitationStatus?: string
}

type TanStackDataGridProps = {
  navigate: NavigateFunction
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  entityType: EntityType
  pageSize: number
  onSelectionChange?: (selectedEntities: readonly EntityWithLabelAndRelated[]) => void
}

const sortHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  cursor: 'pointer',
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'inherit',
}

const TanStackDataGrid = ({
  navigate,
  entityTypes,
  entityType,
  pageSize,
  onSelectionChange,
}: TanStackDataGridProps) => {
  const { hasPermission } = useHasGlobalPermissions(GlobalAction.ManageAuthentication)

  const showAuthenticationInfo =
    entityType.kind.equals(EntityTypeKind.Companion.User) && hasPermission(GlobalAction.ManageAuthentication)

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery] = useDebounced(searchQuery)

  const [columnFilters, setColumnFilters] = useState<Map<FieldId, string>>(new Map())
  const [filterLabels, setFilterLabels] = useState<Map<FieldId, string>>(new Map())
  const [debouncedFilters] = useDebounced(columnFilters)

  const totalCount = useEntityCount({
    typeId: entityType.id,
    searchQuery: debouncedSearchQuery,
    filters: debouncedFilters,
  })

  const { entities, loadMore, reload, hasMore, loading } = usePaginatedEntitiesOfType({
    typeId: entityType.id,
    searchQuery: debouncedSearchQuery,
    filters: debouncedFilters,
    allTypes: entityTypes,
    pageSize,
  })

  const entityIdMap = useMemo(() => getIdMap(entities), [entities])

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [sorting, setSorting] = useState<SortingState>([])
  const [openBulkEditFieldId, setOpenBulkEditFieldId] = useState<FieldId | null>(null)
  const [openFilterFieldId, setOpenFilterFieldId] = useState<FieldId | null>(null)

  const entityTypeKey = entityType.id.toString()

  useEffect(() => {
    setRowSelection({})
  }, [entityTypeKey, searchQuery, columnFilters])

  useEffect(() => {
    setColumnFilters(new Map())
    setFilterLabels(new Map())
  }, [entityTypeKey])

  const handleFilterChange = (fieldId: FieldId, value: string | null, label?: string) => {
    setColumnFilters((prev) => {
      const next = new EqualityHashMap(prev)
      if (value === null) {
        next.delete(fieldId)
      } else {
        next.set(fieldId, value)
      }
      return next
    })
    setFilterLabels((prev) => {
      const next = new EqualityHashMap(prev)
      if (value === null || !label) {
        next.delete(fieldId)
      } else {
        next.set(fieldId, label)
      }
      return next
    })
  }

  const handleClearFilter = (fieldId: FieldId) => {
    handleFilterChange(fieldId, null)
  }

  const handleClearAllFilters = () => {
    setColumnFilters(new Map())
    setFilterLabels(new Map())
  }

  const formatFilterValue = (value: string, field: Field): string => {
    if (value === NULL_FILTER_VALUE) return 'empty'
    const label = filterLabels.get(field.id)
    if (label) return label
    return value
  }

  const tableData: TableRowData[] = useMemo(
    () =>
      entities.map((entity, index) => {
        const fieldValues = Object.fromEntries(
          entityType.fields.asJsReadonlyArrayView().map((field) => [
            `field-${field.id.toString()}`,
            entity.entity.valuesByFieldId
              .asJsReadonlyMapView()
              .get(field.id)
              ?.let((it) => fieldValueToString(it)),
          ])
        )

        const sortValues = Object.fromEntries(
          entityType.fields.asJsReadonlyArrayView().map((field) => {
            const fieldKey = `field-${field.id.toString()}`
            const rawValue = fieldValues[fieldKey]

            if (field.type === FieldType.Reference && rawValue) {
              const refId = entityIdFromStringOrNull(rawValue)
              if (refId) {
                const refEntity = entityIdMap.get(refId)
                return [fieldKey, refEntity?.primaryLabelText ?? rawValue]
              }
            }
            if (field.type === FieldType.ReferenceSet) {
              return [fieldKey, String(rawValue ? rawValue.split(', ').length : 0)]
            }
            return [fieldKey, rawValue]
          })
        )

        return {
          entityId: entity.entity.id.toString(),
          rowNumber: index + 1,
          entity,
          fieldValues,
          sortValues,
          ...(showAuthenticationInfo
            ? {
                phoneNumberLoginIdentifier:
                  entity.entity.externalValues.asJsReadonlyMapView().get(phoneNumberLoginIdentifierKey) || '',
                emailLoginIdentifier:
                  entity.entity.externalValues.asJsReadonlyMapView().get(emailLoginIdentifierKey) || '',
                invitationStatus: entity.entity.externalValues.asJsReadonlyMapView().get(invitationStatusKey) || '',
              }
            : {}),
        }
      }),
    [entities, entityType.fields, showAuthenticationInfo, entityIdMap]
  )

  const entityIdMapRef = useRef(entityIdMap)
  entityIdMapRef.current = entityIdMap

  const reloadRef = useRef(reload)
  reloadRef.current = reload

  const selectedEntityIds = useMemo(() => Object.keys(rowSelection), [rowSelection])

  const columns = useMemo<ColumnDef<TableRowData>[]>(() => {
    const fieldColumns: ColumnDef<TableRowData>[] = entityType.fields.asJsReadonlyArrayView().map((field) => ({
      id: `field-${field.id.toString()}`,
      header: ({ column }) => {
        // Text fields only support exact match filtering, which would be confusing alongside the fuzzy search
        const isFilterable = field.type !== FieldType.Text && field.type !== FieldType.ReferenceSet
        return (
          <>
            <button
              type="button"
              key="label"
              style={{ ...sortHeaderStyle, minWidth: 0, flex: 1, paddingRight: isFilterable ? 56 : 32 }}
              onClick={column.getToggleSortingHandler()}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{field.name}</span>
              <SortIndicator direction={column.getIsSorted()} />
            </button>
            {isFilterable && (
              <FilterPopover
                key="filter"
                field={field}
                currentValue={columnFilters.get(field.id) ?? null}
                onApply={(value, label) => handleFilterChange(field.id, value, label)}
                onClear={() => handleClearFilter(field.id)}
                opened={openFilterFieldId?.equals(field.id) ?? false}
                onClose={() => setOpenFilterFieldId(null)}
                entityTypes={entityTypes}
                target={
                  <IconButton
                    size="sm"
                    color={columnFilters.has(field.id) ? 'platform' : 'neutral'}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenFilterFieldId((prev) => (prev?.equals(field.id) ? null : field.id))
                    }}
                    style={{ position: 'absolute', right: 32 }}
                    aria-label={`Filter by ${field.name}`}
                  >
                    <IconFilter size={iconSizes.sm} stroke={1.5} />
                  </IconButton>
                }
              />
            )}
            {field.type !== FieldType.ReferenceSet && (
              <BulkEditMenu
                key="menu"
                field={field}
                opened={openBulkEditFieldId?.equals(field.id) ?? false}
                onClose={() => setOpenBulkEditFieldId(null)}
                selectedEntityIds={selectedEntityIds}
                entityTypes={entityTypes}
                onEditComplete={() => {
                  reloadRef.current()
                  setRowSelection({})
                }}
                target={
                  <IconButton
                    size="sm"
                    color="neutral"
                    onClick={(e) => {
                      e.stopPropagation()
                      setOpenBulkEditFieldId((prev) => (prev?.equals(field.id) ? null : field.id))
                    }}
                    style={{ position: 'absolute', right: 8 }}
                    aria-label={`Menu for ${field.name}`}
                  >
                    <IconDotsVertical size={iconSizes.sm} stroke={1.5} />
                  </IconButton>
                }
              />
            )}
          </>
        )
      },
      accessorFn: (row) => row.fieldValues[`field-${field.id.toString()}`],
      cell: (info) => {
        const cellContent: CellContent = { value: info.getValue() as string | undefined }
        const renderCell = getRenderFieldCell(field.type, entityIdMapRef.current, entityTypes)
        return renderCell({ value: cellContent })
      },
      sortingFn: naturalSortingFn,
      size: 200,
      minSize: 100,
    }))

    const baseColumns: ColumnDef<TableRowData>[] = [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
            aria-label="Select all rows"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Select row ${row.original.rowNumber}`}
          />
        ),
        size: 50,
        enableResizing: false,
        enableSorting: false,
      },
      {
        id: 'row',
        header: ({ column }) => (
          <button type="button" style={sortHeaderStyle} onClick={column.getToggleSortingHandler()}>
            <span>Row</span>
            <SortIndicator direction={column.getIsSorted()} />
          </button>
        ),
        accessorKey: 'rowNumber',
        sortingFn: 'basic',
        size: 70,
        minSize: 50,
      },
    ]

    const authColumns: ColumnDef<TableRowData>[] = showAuthenticationInfo
      ? [
          {
            id: 'phoneNumberLoginIdentifier',
            header: ({ column }) => (
              <button type="button" style={sortHeaderStyle} onClick={column.getToggleSortingHandler()}>
                <span>Access Phone Number</span>
                <SortIndicator direction={column.getIsSorted()} />
              </button>
            ),
            accessorKey: 'phoneNumberLoginIdentifier',
            sortingFn: naturalSortingFn,
            size: 180,
            minSize: 100,
          },
          {
            id: 'emailLoginIdentifier',
            header: ({ column }) => (
              <button type="button" style={sortHeaderStyle} onClick={column.getToggleSortingHandler()}>
                <span>Access Email</span>
                <SortIndicator direction={column.getIsSorted()} />
              </button>
            ),
            accessorKey: 'emailLoginIdentifier',
            sortingFn: naturalSortingFn,
            size: 180,
            minSize: 100,
          },
          {
            id: 'invitationStatus',
            header: ({ column }) => (
              <button type="button" style={sortHeaderStyle} onClick={column.getToggleSortingHandler()}>
                <span>Invitation</span>
                <SortIndicator direction={column.getIsSorted()} />
              </button>
            ),
            accessorKey: 'invitationStatus',
            sortingFn: naturalSortingFn,
            size: 100,
            minSize: 80,
            cell: (info) => {
              const renderCell = getRenderInvitationCell(() => reloadRef.current())
              return renderCell({
                value: info.getValue() as string,
                row: {
                  id: info.row.original.entityId,
                  phoneNumberLoginIdentifier: info.row.original.phoneNumberLoginIdentifier,
                  emailLoginIdentifier: info.row.original.emailLoginIdentifier,
                },
              })
            },
          },
        ]
      : []

    return [...baseColumns, ...fieldColumns, ...authColumns]
  }, [
    entityType.fields,
    entityTypes,
    showAuthenticationInfo,
    columnFilters,
    openFilterFieldId,
    openBulkEditFieldId,
    selectedEntityIds,
  ])

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      rowSelection,
      columnSizing,
      sorting,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.entityId,
  })

  const selectedEntities = useMemo(
    () => entities.filter((entity) => rowSelection[entity.entity.id.toString()]),
    [entities, rowSelection]
  )

  useEffect(() => {
    onSelectionChange?.(selectedEntities)
  }, [selectedEntities, onSelectionChange])

  return (
    <TablePanel style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Group
        gap="md"
        px="xs"
        py="xs"
        wrap="nowrap"
        style={{
          backgroundColor: 'var(--trellis-color-surface)',
          borderBottom: '1px solid var(--trellis-color-outline)',
          flexShrink: 0,
        }}
      >
        <TextInput
          placeholder="Search"
          aria-label="Search"
          size="sm"
          value={searchQuery}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(event.target.value)
          }}
          style={{ minWidth: 150, flex: 1, maxWidth: 300 }}
          data-testid="entity-search-input"
        />
        <Text data-testid="entity-selected-rows-text" size="sm" c="platform" style={{ whiteSpace: 'nowrap' }}>
          {selectedEntityIds.length > 0
            ? `${selectedEntityIds.length} row${selectedEntityIds.length === 1 ? '' : 's'} selected`
            : ''}
        </Text>
        <div style={{ flexGrow: 1 }} />
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {totalCount !== undefined ? `${totalCount} total` : 'Loading count...'}
        </Text>
      </Group>
      {columnFilters.size > 0 && (
        <Group
          gap="xs"
          px="xs"
          py="xxs"
          style={{
            flexWrap: 'wrap',
            backgroundColor: 'var(--trellis-color-surface)',
            borderBottom: '1px solid var(--trellis-color-outline)',
          }}
        >
          {Array.from(columnFilters.entries()).map(([fieldId, value]) => {
            const field = entityType.fields.asJsReadonlyArrayView().find((f) => f.id.equals(fieldId))
            return field ? (
              <Chip
                key={fieldId.toString()}
                data-testid="grid-filter-chip"
                tone="platform"
                size="sm"
                onRemove={() => handleClearFilter(fieldId)}
              >
                {`${field.name}: ${formatFilterValue(value, field)}`}
              </Chip>
            ) : null
          })}
          <Button
            data-testid="grid-clear-filters-chip"
            variant="subtle"
            size="compact-sm"
            onClick={handleClearAllFilters}
          >
            Clear all
          </Button>
        </Group>
      )}
      <InfiniteScrollTable
        table={table}
        loading={loading}
        hasMore={hasMore}
        loadMore={loadMore}
        emptyMessage="No entities found"
        containerTestId="entity-table-container"
        onRowClick={(row) => {
          const id = entityIdFromString(row.id)
          if (id) navigate(`/entities/${entityType.id.toString()}/view/${id.toString()}`)
        }}
      />
    </TablePanel>
  )
}

export default TanStackDataGrid
